const DEMO_SESSION_KEY = "demo_session_state";

const DEFAULT_FEATURES = ["smartscan", "smartlist", "pos"];

const normalizeFeature = (value = "") =>
  value.toString().trim().toLowerCase().replace(/\s+/g, "");

export function createDefaultDemoSession() {
  return {
    plan: "Free",
    selected_features: [...DEFAULT_FEATURES],
    email: "",
    name: "",
    signup_context: "",
    checkout_status: "free",
    session_id: "",
    remaining_demo_feature: {
      smartscan: true,
      smartlist: true,
      pos: true,
    },
    completed_features: [],
    updated_at: Date.now(),
  };
}

export function loadDemoSession() {
  try {
    const raw = localStorage.getItem(DEMO_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

export function saveDemoSession(input) {
  const fallback = createDefaultDemoSession();
  const selected = Array.isArray(input?.selected_features)
    ? input.selected_features.map(normalizeFeature).filter(Boolean)
    : fallback.selected_features;

  const normalizedSelected = [...new Set(selected)];
  const remainingFromInput = input?.remaining_demo_feature || {};
  const completedFromInput = Array.isArray(input?.completed_features)
    ? input.completed_features.map(normalizeFeature)
    : [];

  const remaining_demo_feature = {
    smartscan:
      typeof remainingFromInput.smartscan === "boolean"
        ? remainingFromInput.smartscan
        : !completedFromInput.includes("smartscan"),
    smartlist:
      typeof remainingFromInput.smartlist === "boolean"
        ? remainingFromInput.smartlist
        : !completedFromInput.includes("smartlist"),
    pos:
      typeof remainingFromInput.pos === "boolean"
        ? remainingFromInput.pos
        : !completedFromInput.includes("pos"),
  };

  const state = {
    plan: input?.plan || fallback.plan,
    selected_features: normalizedSelected.length
      ? normalizedSelected
      : fallback.selected_features,
    email: input?.email || "",
    name: input?.name || "",
    signup_context: input?.signup_context || "",
    checkout_status: input?.checkout_status || "free",
    session_id: input?.session_id || "",
    remaining_demo_feature,
    completed_features: Object.entries(remaining_demo_feature)
      .filter(([, remaining]) => remaining === false)
      .map(([featureId]) => featureId),
    updated_at: Date.now(),
  };

  localStorage.setItem(DEMO_SESSION_KEY, JSON.stringify(state));
  return state;
}

export function markDemoFeatureCompleted(featureId) {
  const normalized = normalizeFeature(featureId);
  const current = loadDemoSession() || createDefaultDemoSession();
  const remaining = {
    ...(current.remaining_demo_feature || {}),
    [normalized]: false,
  };

  return saveDemoSession({
    ...current,
    remaining_demo_feature: remaining,
    completed_features: Object.entries(remaining)
      .filter(([, isRemaining]) => isRemaining === false)
      .map(([id]) => id),
  });
}

export function buildDemoSessionFromCheckout({
  plan,
  selected_features,
  email,
  name,
  signup_context,
  checkout_status,
  session_id,
  remaining_demo_feature,
  completed_features,
}) {
  return saveDemoSession({
    plan: plan || "Free",
    selected_features:
      Array.isArray(selected_features) && selected_features.length
        ? selected_features
        : DEFAULT_FEATURES,
    email: email || "",
    name: name || "",
    signup_context: signup_context || "",
    checkout_status: checkout_status || "free",
    session_id: session_id || "",
    remaining_demo_feature,
    completed_features,
  });
}

export { DEMO_SESSION_KEY, DEFAULT_FEATURES };
